// DocumentRichTextSection.jsx
//
// Shared richtext section for document viewers (Terms, Memo, etc.).

import React, { useCallback, useMemo } from 'react';

// Utilities
import { buildNodeFieldPatch, readNodeFieldRawValue } from 'utilities/NodeFieldData';
import { extractRichTextPlainText } from 'utilities/RichText';
import { getResolvedFields } from 'utilities/NodeType';

// CSS
import './DocumentRichTextSection.css';

// Components
import NodeRichTextField from 'node/form/fields/richtext/NodeRichTextField';

function normalizeString(value) {
  return String(value || '').trim();
}

/**
 * @param {object|null|undefined} nodeType
 * @param {string} fieldName
 * @returns {object|null}
 */
function getSchemaField(nodeType, fieldName) {
  const name = normalizeString(fieldName);
  if (!name) return null;
  const fields = Array.isArray(nodeType?.schema?.fields)
    ? nodeType.schema.fields
    : (getResolvedFields(nodeType?.key, { includeHidden: true }) || []);
  return fields.find((field) => normalizeString(field?.name) === name) || null;
}

/**
 * DocumentRichTextSection
 *
 * @param {object} props
 * @param {object} props.node
 * @param {object|null} [props.nodeType]
 * @param {boolean} [props.readOnly=false]
 * @param {function|null} [props.onNodeChange]
 * @param {string} props.fieldName
 * @param {string} props.title
 * @param {string} [props.emptyLabel]
 */
const DocumentRichTextSection = React.memo(({
  node,
  nodeType = null,
  readOnly = false,
  onNodeChange = null,
  fieldName,
  title,
  emptyLabel = null,
}) => {
  const canEdit = Boolean(!readOnly && typeof onNodeChange === 'function' && node?.key);
  const typeKey = normalizeString(nodeType?.key || node?.type).toUpperCase();
  const resolvedTitle = normalizeString(title || fieldName) || 'Notes';
  const resolvedEmptyLabel = normalizeString(emptyLabel) || `No ${resolvedTitle.toLowerCase()}.`;

  const field = useMemo(() => {
    const schemaField = getSchemaField(nodeType, fieldName);
    if (schemaField) return schemaField;
    return {
      name: fieldName,
      label: resolvedTitle,
      type: 'richtext',
      source: typeKey.toLowerCase(),
    };
  }, [fieldName, nodeType, resolvedTitle, typeKey]);

  const value = useMemo(
    () => readNodeFieldRawValue(node, field, typeKey),
    [field, node, typeKey],
  );

  const isEmpty = !extractRichTextPlainText(value);

  const handleChange = useCallback((nextValue) => {
    if (!canEdit || !node?.key) return;
    const patch = buildNodeFieldPatch(node, field, typeKey, nextValue);
    if (!patch) return;
    onNodeChange(node.key, patch);
  }, [canEdit, field, node, onNodeChange, typeKey]);

  if (!node || !fieldName) return null;

  return (
    <div className="document-rich-text-section">
      <div className="document-rich-text-section-header">
        <div className="document-rich-text-section-title">
          {resolvedTitle}
        </div>
      </div>

      <div className="document-rich-text-section-body">
        {canEdit ? (
          <NodeRichTextField
            field={field}
            fieldLabel={resolvedTitle}
            value={value}
            onChange={handleChange}
          />
        ) : (
          isEmpty ? (
            <div className="document-rich-text-section-empty">
              {resolvedEmptyLabel}
            </div>
          ) : (
            <NodeRichTextField
              field={field}
              fieldLabel={resolvedTitle}
              value={value}
              readOnly
            />
          )
        )}
      </div>
    </div>
  );
});

DocumentRichTextSection.displayName = 'DocumentRichTextSection';

export default DocumentRichTextSection;
